import React, { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Contract, Milestone } from '../../types';
import {
  canIncludeStageInVariation,
  validStageVariationAmounts,
  stageVariationOre,
} from '../../utils/stageVariation';
import { wholeKronorInput } from '../../utils/wholeKronor';
import { formatKronor } from './milestonePlan';
import type { VariationPlan } from './useVariationPlan';
import type { VariationRow } from '../../hooks/useStageVariationAutosave';

export const variationMoney = (ore: number) => `${formatKronor(ore / 100)} kr`;
export function VariationTotals({
  parent,
  root,
  grossDeltaOre,
  netDeltaOre,
}: {
  parent: Contract;
  root: Contract;
  grossDeltaOre: number;
  netDeltaOre: number;
}) {
  const { t } = useTranslation();
  const before = Math.round(Number(parent.afterRotAmount) * 100);
  const base = Math.round(Number(root.afterRotAmount) * 100);
  return (
    <section
      className="variation-wizard-totals mb-4"
      aria-label={t('variationWizard.amounts')}
    >
      <div className="row g-3">
        <div className="col-12 col-sm-6">
          <div className="border rounded p-3 h-100 bg-primary-subtle">
            <h3 className="fs-6">{t('variationWizard.thisChange')}</h3>
            <strong className="fs-3">{variationMoney(netDeltaOre)}</strong>
            <p className="small mb-0">{t('variationWizard.afterRot')}</p>
            <p className="small mb-0 mt-2">
              {t('variationWizard.grossChange')}:{' '}
              {variationMoney(grossDeltaOre)}
              <br />
              {t('variationWizard.rotChange')}:{' '}
              {variationMoney(grossDeltaOre - netDeltaOre)}
            </p>
          </div>
        </div>
        <div className="col-12 col-sm-6">
          <div className="border rounded p-3 h-100">
            <h3 className="fs-6">{t('variationWizard.projectAfter')}</h3>
            <strong className="fs-3">
              {variationMoney(before + netDeltaOre)}
            </strong>
            <p className="small mb-0">
              {t('variationWizard.before')}: {variationMoney(before)}{' '}
              {t('variationWizard.afterRot')}
            </p>
          </div>
        </div>
      </div>
      <div className="d-flex flex-wrap gap-2 small border rounded p-3 mt-3">
        <span>
          {t('variationWizard.base')}: <strong>{variationMoney(base)}</strong>
        </span>
        <span aria-hidden="true">+</span>
        <span>
          {t('variationWizard.previousChanges')}:{' '}
          <strong>{variationMoney(before - base)}</strong>
        </span>
        <span aria-hidden="true">+</span>
        <span>
          {t('variationWizard.thisChange')}:{' '}
          <strong>{variationMoney(netDeltaOre)}</strong>
        </span>
        <span aria-hidden="true">=</span>
        <strong>{variationMoney(before + netDeltaOre)}</strong>
      </div>
    </section>
  );
}

function RowFields({
  row,
  plan,
  original,
}: {
  row: VariationRow;
  plan: VariationPlan;
  original?: Milestone;
}) {
  const { t } = useTranslation();
  const ids = useId();
  const amount = stageVariationOre(row.amount);
  const gross = stageVariationOre(row.grossAmount);
  const rot = useRef(Math.abs(gross - amount) || 0);
  // Preserve the deduction even while the user clears and retypes an amount.
  if (amount !== 0 && validStageVariationAmounts(gross, amount))
    rot.current = Math.abs(gross - amount);
  const [changeInput, setChangeInput] = useState<string | null>(null);
  const nameInvalid = !row.description.trim();
  const amountInvalid = !Number.isSafeInteger(amount);
  const rotInvalid = !validStageVariationAmounts(gross, amount);
  const invalid = plan.showErrors && (nameInvalid || amountInvalid || rotInvalid);
  const money = (field: 'amount' | 'grossAmount', value: string) => {
    const next = wholeKronorInput(value, { allowNegative: true });
    if (field === 'grossAmount') {
      plan.patch(row.key, { grossAmount: next.value });
      return;
    }
    setChangeInput(null);
    changeAmount(next.value);
  };
  const changeAmount = (value: string) => {
    const nextAmount = stageVariationOre(value);
    plan.patch(row.key, {
      amount: value,
      grossAmount: Number.isSafeInteger(nextAmount)
        ? String((nextAmount === 0 ? 0 : nextAmount + Math.sign(nextAmount) * rot.current) / 100)
        : '',
    });
  };
  const priceChange = (value: string) => {
    const next = wholeKronorInput(value, { allowNegative: true }).value;
    setChangeInput(next);
    const delta = stageVariationOre(next);
    changeAmount(Number.isSafeInteger(delta)
      ? String((Math.round(original!.afterRotAmount * 100) + delta) / 100)
      : '');
  };
  return (
    <div className="row g-3 mt-0" data-testid={`variation-row-${row.key}`}>
      <div className="col-12 col-lg-5">
        <label className="form-label small" htmlFor={`${ids}-name`}>
          {t('variationWizard.stageName')}
        </label>
        <input
          id={`${ids}-name`}
          className={`form-control${plan.showErrors && nameInvalid ? ' is-invalid' : ''}`}
          value={row.description}
          onChange={(e) => plan.patch(row.key, { description: e.target.value })}
          maxLength={4000}
        />
      </div>
      <div className="col-12 col-lg-3">
        <label className="form-label small" htmlFor={`${ids}-amount`}>
          {t('variationWizard.amountAfterRot')}
        </label>
        <input
          id={`${ids}-amount`}
          className={`form-control${plan.showErrors && amountInvalid ? ' is-invalid' : ''}`}
          inputMode="text"
          value={row.amount}
          onChange={(e) => money('amount', e.target.value)}
        />
        {original && (
          <small className="text-muted">
            {t('variationWizard.before')}:{' '}
            {variationMoney(Math.round(original.afterRotAmount * 100))}
          </small>
        )}
      </div>
      <div className="col-12 col-lg-4">
        <label className="form-label small" htmlFor={`${ids}-date`}>
          {t('variationWizard.endDate')}
        </label>
        <input
          id={`${ids}-date`}
          type="date"
          className="form-control"
          value={row.endDate}
          onChange={(e) => plan.patch(row.key, { endDate: e.target.value })}
        />
        {original?.endDate && (
          <small className="text-muted">
            {t('variationWizard.before')}: {original.endDate.slice(0, 10)}
          </small>
        )}
      </div>
      {original && (
        <div className="col-12">
          <label className="form-label small" htmlFor={`${ids}-change`}>
            {t('variationWizard.priceAdjustment')}
          </label>
          <input
            id={`${ids}-change`}
            className={`form-control${plan.showErrors && amountInvalid ? ' is-invalid' : ''}`}
            inputMode="text"
            aria-describedby={`${ids}-change-hint`}
            value={changeInput ?? (Number.isSafeInteger(amount)
              ? String((amount - Math.round(original.afterRotAmount * 100)) / 100)
              : '')}
            onChange={(e) => priceChange(e.target.value)}
          />
          <small id={`${ids}-change-hint`} className="text-muted">
            {t('variationWizard.stagePriceChangeHint')}
          </small>
        </div>
      )}
      <div className="col-12">
        <details open={plan.showErrors && rotInvalid ? true : undefined}>
          <summary className="small variation-wizard-touch">
            {t('variationWizard.rotDetails')}
          </summary>
          <label className="form-label small" htmlFor={`${ids}-gross`}>
            {t('variationWizard.amountBeforeRot')}
          </label>
          <input
            id={`${ids}-gross`}
            className={`form-control${plan.showErrors && rotInvalid ? ' is-invalid' : ''}`}
            inputMode="text"
            value={row.grossAmount}
            onChange={(e) => money('grossAmount', e.target.value)}
          />
          <small className="text-muted">{t('variationWizard.rotHint')}</small>
        </details>
      </div>
      {invalid && (
        <p className="text-danger small mb-0" role="alert">
          {t(nameInvalid ? 'variationWizard.stageNameRequired' : amountInvalid
            ? 'variationWizard.invalidStageTotal' : 'stageVariation.invalidStageRot')}
        </p>
      )}
    </div>
  );
}

export function VariationPlanStep({
  parent,
  root,
  sources,
  plan,
  origins,
  entryStageId,
  priceOnly,
  modeLocked = false,
  onModeChange,
  switching,
  priceAmount,
  onPriceAmount,
  priceError,
  restorable = [],
}: {
  parent: Contract;
  root: Contract;
  sources: Milestone[];
  plan: VariationPlan;
  origins: Record<string, string>;
  entryStageId?: string;
  priceOnly: boolean;
  modeLocked?: boolean;
  onModeChange: (checked: boolean) => void;
  switching: boolean;
  priceAmount: string;
  onPriceAmount: (amount: string) => void;
  priceError: string;
  restorable?: Milestone[];
}) {
  const { t } = useTranslation();
  const ids = useId();
  const locked = sources.filter((stage) => !canIncludeStageInVariation(stage));
  const editable = sources.filter(canIncludeStageInVariation);
  const net = priceOnly
    ? stageVariationOre(priceAmount)
    : plan.totals.netDeltaOre;
  return (
    <div className="contract-wizard-step" data-testid="variation-plan">
      <header className="mb-4">
        <p className="contract-wizard-kicker">
          {t('contracts.wizard.stepper.counter', { current: 2, total: 3 })}
        </p>
        <h2 className="contract-wizard-step-title">
          {t('variationWizard.planTitle')}
        </h2>
        <p className="text-muted">{t('variationWizard.planIntro')}</p>
      </header>
      <VariationTotals
        parent={parent}
        root={root}
        grossDeltaOre={priceOnly ? net : plan.totals.grossDeltaOre}
        netDeltaOre={net}
      />
      <div className="form-check form-switch mb-4 variation-wizard-touch">
        <input
          id={`${ids}-price`}
          className="form-check-input"
          type="checkbox"
          checked={priceOnly}
          disabled={modeLocked || switching || plan.autosave.saving || plan.checking}
          onChange={(e) => onModeChange(e.target.checked)}
        />
        <label
          className="form-check-label fw-semibold"
          htmlFor={`${ids}-price`}
        >
          {t('variationWizard.priceOnly')}
        </label>
      </div>
      {modeLocked && <p className="small text-muted">{t('variationWizard.priceModeLocked')}</p>}
      {priceError && (
        <div className="alert alert-danger" role="alert">
          {priceError}
        </div>
      )}
      {priceOnly ? (
        <div className="border rounded p-3">
          <label className="form-label fw-semibold" htmlFor={`${ids}-delta`}>
            {t('variationWizard.priceAdjustment')}
          </label>
          <input
            id={`${ids}-delta`}
            className="form-control"
            inputMode="decimal"
            value={priceAmount}
            onChange={(e) => onPriceAmount(e.target.value)}
            disabled={switching}
          />
          <p className="small text-muted mt-2 mb-0">
            {t('variationWizard.priceOnlyHint')}
          </p>
        </div>
      ) : (
        <>
          {!!locked.length && (
            <details className="border rounded p-3 mb-3" open={undefined}>
              <summary className="fw-semibold variation-wizard-touch">
                {t('variationWizard.lockedStages', { count: locked.length })}
              </summary>
              {locked.map((stage) => (
                <div
                  key={stage.id}
                  className="variation-wizard-locked rounded p-3 mt-2 d-flex flex-wrap justify-content-between gap-2"
                >
                  <span>
                    <i className="feather-lock me-2" aria-hidden="true" />
                    {stage.description}
                    <small className="d-block">
                      {t('variationWizard.lockedHint')}
                    </small>
                  </span>
                  <strong>
                    {variationMoney(Math.round(stage.afterRotAmount * 100))}
                  </strong>
                </div>
              ))}
            </details>
          )}
          <div className="d-flex flex-column gap-3">
            {editable.map((source) => {
              const selected = plan.selectedIds.includes(source.id);
              const isEntryStage = source.id === entryStageId;
              const row = plan.rows.find((item) => item.sourceId === source.id);
              return (
                <section
                  key={source.id}
                  data-testid={`variation-source-${source.id}`}
                  className={`border rounded p-3${isEntryStage ? ' border-warning alert alert-warning mb-0' : selected ? ' border-primary' : ''}`}
                >
                  <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                    <div>
                      <span className="badge bg-light text-dark border me-2">
                        {origins[source.id] ?? t('variationWizard.base')}
                      </span>
                      <strong>{source.description}</strong>
                      {isEntryStage && (
                        <span className="badge bg-warning text-dark ms-2">
                          <i className="bi bi-exclamation-triangle me-1" aria-hidden="true" />
                          {t('variationWizard.objectedStage')}
                        </span>
                      )}
                      {selected && (
                        <span
                          className={`badge ms-2 ${row ? 'bg-primary' : 'bg-danger'}`}
                        >
                          {t(
                            row
                              ? 'variationWizard.changed'
                              : 'variationWizard.omitted',
                          )}
                        </span>
                      )}
                      {!selected && (
                        <div className="small text-muted mt-2">
                          {variationMoney(
                            Math.round(source.afterRotAmount * 100),
                          )}{' '}
                          · {source.endDate?.slice(0, 10) ?? '—'}
                        </div>
                      )}
                      {isEntryStage && (
                        <p className="small mt-2 mb-0">{t('variationWizard.objectionSource')}</p>
                      )}
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                      {!selected ? (
                        <>
                          <button
                            type="button"
                            className="btn btn-outline-primary variation-wizard-touch"
                            disabled={plan.checking}
                            onClick={() => void plan.edit(source)}
                          >
                            {t('variationWizard.change')}
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-danger variation-wizard-touch"
                            disabled={plan.checking}
                            onClick={() => void plan.omit(source)}
                          >
                            {t('variationWizard.omit')}
                          </button>
                        </>
                      ) : (
                        <>
                          {row && (
                            <button
                              type="button"
                              className="btn btn-outline-danger variation-wizard-touch"
                              onClick={() => void plan.omit(source)}
                            >
                              {t('variationWizard.omit')}
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn-outline-secondary variation-wizard-touch"
                            onClick={() => plan.undo(source.id)}
                          >
                            {t('variationWizard.undo')}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {row && <RowFields row={row} plan={plan} original={source} />}
                  {selected && !row && (
                    <p className="small text-muted mt-2 mb-0">
                      {t('variationWizard.omittedHint')}
                    </p>
                  )}
                </section>
              );
            })}
            {plan.rows
              .filter((row) => !row.sourceId)
              .map((row) => (
                <section
                  key={row.key}
                  className="border border-success rounded p-3"
                >
                  <div className="d-flex justify-content-between gap-2">
                    <span className="badge bg-success align-self-center">
                      {t(
                        row.restoresMilestoneId
                          ? 'stageVariation.restoreRemoved'
                          : 'variationWizard.new',
                      )}
                    </span>
                    <button
                      type="button"
                      className="btn btn-outline-danger variation-wizard-touch"
                      onClick={() => plan.remove(row.key)}
                    >
                      {t('variationWizard.remove')}
                    </button>
                  </div>
                  <RowFields row={row} plan={plan} />
                </section>
              ))}
          </div>
          <button
            type="button"
            className="btn btn-outline-primary variation-wizard-touch mt-3"
            onClick={plan.add}
          >
            {t('variationWizard.addStage')}
          </button>
          {!!restorable.length && (
            <details className="border rounded p-3 mt-3">
              <summary className="variation-wizard-touch">
                {t('stageVariation.restoreRemoved')}
              </summary>
              {restorable.map((stage) => (
                <div
                  key={stage.id}
                  className="d-flex flex-wrap justify-content-between gap-2 mt-2"
                >
                  <span>{stage.description}</span>
                  <button
                    type="button"
                    className="btn btn-outline-primary variation-wizard-touch"
                    disabled={plan.rows.some(
                      (row) => row.restoresMilestoneId === stage.id,
                    )}
                    onClick={() => plan.restore(stage)}
                  >
                    {t('stageVariation.restoreRemoved')}
                  </button>
                </div>
              ))}
            </details>
          )}
          {plan.showErrors && !plan.changed && (
            <p className="text-danger mt-3" role="alert">
              {t('variationWizard.changeRequired')}
            </p>
          )}
          <div
            data-testid="variation-autosave-status"
            className="small mt-3"
            aria-live="polite"
          >
            {plan.autosave.error ? (
              <div className="alert alert-danger" role="alert">
                {t('variationWizard.saveError')}{' '}
                <button
                  type="button"
                  className="btn btn-outline-danger variation-wizard-touch"
                  onClick={plan.autosave.retry}
                >
                  {t('variationWizard.retry')}
                </button>
              </div>
            ) : plan.autosave.saving ? (
              t('contracts.wizard.saveStatus.saving')
            ) : !plan.valid ? (
              t('variationWizard.invalidStage')
            ) : plan.autosave.savedAt ? (
              t('contracts.wizard.saveStatus.savedChanges', {
                time: plan.autosave.savedAt?.toLocaleTimeString('sv-SE', {
                  hour: '2-digit',
                  minute: '2-digit',
                }),
              })
            ) : (
              ''
            )}
          </div>
        </>
      )}
    </div>
  );
}
